import * as React from 'react';
import { useController } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFieldErrorTranslator } from './utils';
import { useFormContext } from './Form';

export interface SelectOption<TValue extends string = string> {
  value: TValue;
  label: React.ReactNode;
}

export interface SelectFieldProps<TValue extends string = string> {
  name: string;
  label: React.ReactNode;
  options: ReadonlyArray<SelectOption<TValue>>;
  placeholder?: string;
  disabled?: boolean;
  description?: React.ReactNode;
  containerClassName?: string;
  labelClassName?: string;
  triggerClassName?: string;
  id?: string;
  /**
   * Accessible name for the trigger, when the visible `label` is not specific
   * enough on its own — a form that repeats one label down a column of rows
   * ("În paralel cu", once per operation) reads as seven identical controls to
   * a screen reader. `CheckboxField` gets this free from InputHTMLAttributes;
   * this component takes an explicit prop.
   *
   * Note a hyphenated JSX attribute is exempt from excess-property checking, so
   * without this prop `aria-label` would compile here and simply be dropped.
   */
  'aria-label'?: string;
  /** Fires on user selection only (not on programmatic/form-reset value changes). */
  onValueChange?: (value: string) => void;
}

export function SelectField<TValue extends string = string>({
  name,
  label,
  options,
  placeholder,
  disabled,
  description,
  containerClassName,
  labelClassName,
  triggerClassName,
  id,
  'aria-label': ariaLabel,
  onValueChange,
}: SelectFieldProps<TValue>) {
  const {
    field,
    fieldState: { error },
  } = useController({ name });
  const { clearErrors, trigger } = useFormContext();
  const translate = useFieldErrorTranslator();
  const fieldId = id ?? `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;
  const descId = description ? `${fieldId}-desc` : undefined;
  const errorMessage = translate(error?.message);
  // Radix <Select.Item> forbids empty-string values, but our forms use '' as a
  // "none/clear" sentinel. Translate '' <-> a sentinel internally so callers
  // can keep using '' in their option lists.
  const NONE_SENTINEL = '__none__';
  const toInternal = (v: string) => (v === '' ? NONE_SENTINEL : v);
  const fromInternal = (v: string) => (v === NONE_SENTINEL ? '' : v);
  return (
    <div className={containerClassName}>
      <Label htmlFor={fieldId} className={labelClassName}>
        {label}
      </Label>
      <Select
        value={toInternal((field.value ?? '') as string)}
        onValueChange={(v) => {
          const next = fromInternal(v);
          field.onChange(next);
          if (next) clearErrors(name);
          window.setTimeout(() => void trigger(name), 0);
          onValueChange?.(next);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          id={fieldId}
          className={triggerClassName}
          aria-label={ariaLabel}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={
            [errorMessage ? errorId : undefined, descId].filter(Boolean).join(' ') || undefined
          }
          onBlur={field.onBlur}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={toInternal(opt.value)}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && (
        <p id={descId} className="mt-1 text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {errorMessage && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
